// 頁型模板②：報表／查詢（35 筆 route）。
//
// 與 CrudListPage 的差別（§9.5「不得為了共用把行為明顯不同的 UI 強制合併」）：
//   - 沒有新增／修改／刪除，也沒有編輯用的 ModalFrame；
//   - 查詢條件是「多欄位過濾」而不是單一關鍵字，過濾條件走 ListQuery.filters；
//   - 匯出是主要動作之一（原系統 DataTables buttons 的 excel，common.js:829-854）。
// 兩者確實共用的只有「查詢區 + 表格殼層」這個結構，而那已經是第二層元件了，
// 所以這裡不再多包一層 CrudOrReportPage。
//
// 原系統對應：報表頁一樣是 `.ibox-title` + `.ibox-content` + DataTables 表格，
// 表格 chrome（每頁筆數／關鍵字／筆數資訊／分頁／匯出）全部由 common.js
// `util.DataTableInitByCustomConfig`（:795-865）產生 → <DataTableShell>。
//
// 支援的 scenario（§5.2）：default / empty / filter / pagination / export
// 共通資料狀態：loading / error / unauthorized
//
// 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】

import { type ReactElement, useEffect, useRef, useState } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import {
  ContentPanel,
  DataTableShell,
  FormGrid,
  FormGridField,
  SearchPanel,
  useDialogs,
} from '@/components/layouts';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { t } from '@/messages/zh-TW';
import {
  columnsFor,
  DEFAULT_PAGE_SIZE,
  initialValues,
  legacyContractFieldsFor,
  legacyTableSequenceFor,
  resolveAllLegacyMessages,
  resolveLegacyMessage,
  resolveLegacyMessageForAction,
  toPayload,
  useDataSource,
  useList,
  useMessageKeyOverride,
  useScenario,
} from '@/mocks';
import { LegacyContractFields } from './LegacyContractFields';
import { pageBehavior } from './page-behaviors';
import {
  DialogHost,
  ExtraValidationMessages,
  type PageColumn,
  rowUiKey,
  TemplateError,
  toUiKey,
} from './shared';
import type { PageTemplateProps } from './types';

/** 查詢條件用的下拉欄位：fixture 四種 shape 都有一個 status 欄。沒有就只留關鍵字。 */
function filterColumnOf(columns: readonly PageColumn[]): PageColumn | undefined {
  return columns.find((column) => column.kind === 'status');
}

export function ReportPage({ route }: PageTemplateProps): ReactElement {
  return (
    <DialogHost route={route}>
      <ReportBody route={route} />
    </DialogHost>
  );
}

export function ReportBody({ route }: PageTemplateProps) {
  const pageId = route.pageId;
  // 批次 14（Gitea issue #22）：report 頁型的整頁自訂 body（匯出頁/查詢統計頁/academicMOM）；
  // 未登錄頁零影響（complexBody 同款 opt-in）。放在 hooks 之前。
  const reportBehavior = pageBehavior(pageId);
  if (reportBehavior?.reportBody !== undefined) {
    return reportBehavior.reportBody({ pageId });
  }
  // 批次 21：generic 內容拆出 ReportGenericBody（模板行為測試直渲染，與 REGISTRY 解耦；
  // 全部 35 個 report 頁註冊 reportBody 後已無未註冊頁可指名）。
  return <ReportGenericBody route={route} />;
}

/** 批次 21：generic report 模板本體（無 registry 檢查；ReportPage.test/data-effect.test 直渲染）。 */
export function ReportGenericBody({ route }: PageTemplateProps) {
  const pageId = route.pageId;
  const scenario = useScenario();
  const dialogs = useDialogs();
  const dataSource = useDataSource();
  // FC-5 R4（C1-c‴）：見 CrudListPage.tsx 同名變數的說明。
  const messageKeyOverride = useMessageKeyOverride();
  // FC-5 R2（C1-c′）：這頁專屬的空表文案（沒有就是 null，DataTableShell 退回雛形預設）。
  const legacyEmptyMessage = resolveLegacyMessage(pageId, 'empty', messageKeyOverride);
  const columns = columnsFor(pageId);
  const filterColumn = filterColumnOf(columns);
  // FC-3：原系統該頁主體區的實測欄位（含 name）。legacySearchGroupsFor() 的退路
  // （只涵蓋第一個查詢 form 的舊路徑）已刪除——legacy-fallback-dead-path.test.ts
  // 逐頁驗證過全站 156 頁沒有一頁需要它。
  const contractFields = legacyContractFieldsFor(pageId);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [keyword, setKeyword] = useState('');
  const [draftKeyword, setDraftKeyword] = useState('');
  /** 已套用的下拉過濾值；空字串＝不過濾。 */
  const [filterValue, setFilterValue] = useState('');
  const [draftFilterValue, setDraftFilterValue] = useState('');
  /** 契約欄位的草稿值（還沒按「查詢」）。 */
  const [draftFieldValues, setDraftFieldValues] = useState<Record<string, string>>(() =>
    initialValues(contractFields ?? [], pageId),
  );
  /** 已套用的契約欄位條件，真的會進 `dataSource.list()` 的 `query.filters`（criteria C5-h）。 */
  const [fieldFilters, setFieldFilters] = useState<Record<string, string>>({});
  // FC-5 R3（C1-c″）：這頁的查詢條件驗證訊息（例如「請選擇開始時間!」）——原系統的查詢頁
  // 本來就會擋「沒選查詢條件就送出」，雛形之前沒做這個檢查。純前端檢查，不打資料源
  // （原系統也是送出前就擋掉，不會先打一次後端才發現條件不全）。scenario 決定要不要顯示，
  // 對齊既有模板「scenario 直接呈現那個狀態」的慣例（不用另開一次點擊才看得到）。
  const filterValidationMessages =
    scenario === 'validation-error' ? resolveAllLegacyMessages(pageId, 'validation').map((item) => item.text) : [];

  const filters = {
    ...(filterColumn === undefined || filterValue === ''
      ? {}
      : { [filterColumn.key]: filterValue }),
    ...fieldFilters,
  };
  const { data, loading, error, reload } = useList(pageId, { page, pageSize, keyword, filters });
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  function applyFilters(nextKeyword: string, nextFilterValue: string) {
    setKeyword(nextKeyword);
    setDraftKeyword(nextKeyword);
    setFilterValue(nextFilterValue);
    setDraftFilterValue(nextFilterValue);
    setPage(1);
  }

  /** 按下「查詢」：契約欄位的草稿值成為真正送進 DataSource 的 `filters`。 */
  function applySearch() {
    applyFilters(draftKeyword, draftFilterValue);
    setFieldFilters(toPayload(draftFieldValues, contractFields, pageId));
  }

  function resetSearch() {
    applyFilters('', '');
    setDraftFieldValues(initialValues(contractFields ?? [], pageId));
    setFieldFilters({});
  }

  /**
   * 原系統這顆鈕會由 DataTables buttons 產生 .xlsx 下載；雛形不引 DataTables／jszip（§3.2），
   * 拿到資料後仍只跳一則說明。
   *
   * 但 D-1 第 2a 點規定 `list:<resource>#export` 對應
   * `dataSource.list(resource, { ...query, export: true })`，
   * 所以**必須真的打資料源** —— 只跳提示的按鈕不得標 `data-effect`（criteria C5-j）。
   */
  async function exportExcel() {
    try {
      await dataSource.list(pageId, { page, pageSize, keyword, filters, export: true });
    } catch {
      // FC-5 R3：匯出失敗用它自己專屬的動作關鍵字 'export'，不跟一般查詢失敗共用。
      await dialogs.alert(
        resolveLegacyMessageForAction(pageId, 'failure', 'export', messageKeyOverride)?.text ??
          t('error.generic'),
      );
      return;
    }
    await dialogs.info(t('export.notice'));
  }

  // ── scenario 預設集：filter / pagination / export 要能直接看到那個狀態 ──
  // filter 用「第一列在過濾欄位上的值」當條件：不寫死字串，任何 resource 都保證至少命中一列。
  const autoRan = useRef(false);
  // 相依陣列刻意不完整：補齊之後這段會在使用者改查詢條件時重跑，把條件蓋回 scenario 的預設值。
  // autoRan 保證整段只生效一次。
  // biome-ignore lint/correctness/useExhaustiveDependencies: 見上方說明
  useEffect(() => {
    if (autoRan.current || loading || error !== null) {
      return;
    }
    if (scenario === 'export') {
      autoRan.current = true;
      void exportExcel();
      return;
    }
    if (scenario === 'pagination') {
      autoRan.current = true;
      if (total > pageSize) {
        setPage(2);
      }
      return;
    }
    if (scenario === 'filter') {
      const first = rows[0];
      if (first === undefined || filterColumn === undefined) {
        return;
      }
      const value = first[filterColumn.key];
      if (value === undefined || value === null) {
        return;
      }
      autoRan.current = true;
      applyFilters('', String(value));
    }
  }, [scenario, loading, error, rows, total, pageSize]);

  /** 下拉可選值：欄位定義自帶的 pool，沒有就取目前這頁出現過的值。 */
  const filterOptions =
    filterColumn === undefined
      ? []
      : (filterColumn.pool ??
        [...new Set(rows.map((row) => String(row[filterColumn.key] ?? '')))].filter(
          (value) => value !== '',
        ));

  // FC-3：有實測表頭序列時逐格照抄（含空字串欄的位置），模板不得增刪重排任何一欄。
  // 報表頁沒有行動作，空字串欄就是一格空儲存格——那是原系統本來的形狀，
  // 憑空補一個「操作」欄反而會讓整條序列錯位（設計 A.5）。
  const alignOf = (key: string): { align?: 'right' } =>
    columns.find((column) => column.key === key)?.kind === 'score'
      ? { align: 'right' as const }
      : {};
  const sequence = legacyTableSequenceFor(pageId);
  const tableColumns: readonly TableColumn[] =
    sequence === undefined
      ? columns.map((column) => ({
          key: column.key,
          header: column.label,
          ...(column.kind === 'score' ? { align: 'right' as const } : {}),
        }))
      : sequence.map((column) => ({
          key: column.key,
          header: column.header,
          ...alignOf(column.key),
        }));

  return (
    // 模板只負責內容區：全站外框（ApplicationShell／Navbar／SidebarMenu／Breadcrumb）
    // 由路由層的 AppLayout 提供，模板再包一層會變成殼中殼。
    // 這個 <div> 是整頁的根節點，data-ui-id 就是 §10.1 說的 pageId 本身。
    <div data-ui-id={pageId}>
      {/*
        FC-4：原系統該頁的對話框（trigger tier A–D）。
        trigger 鈕帶 `data-modal-trigger`（FUNCTIONAL-CONTRACT.md D-3），
        對話框本身條件渲染 —— 唯讀 GET 抽不到是**正確結果**，React 側證據一律走
        `tests/react/extract-modals.spec.ts` 的互動擷取。
        三級（global／shared／unique）都由這裡畫：本專案沒有 AppLayout 這一層，
        而三級的內容全部來自同一份 legacy 實測資料，分開手抄只會多一次抄錯的機會。
      */}
      <LegacyContractModals pageId={pageId} />
      <ContentPanel
        uiId={`${pageId}-panel`}
        title={route.title}
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        <SearchPanel
          uiId={`${pageId}-search`}
          busy={loading}
          // D-1：「查詢」真的會打 `dataSource.list(pageId, …)`（透過 useList）；
          // 「清除」只清 state，不標（criteria C5-j）。
          searchEffect={`list:${pageId}`}
          onSearch={() => {
            applySearch();
            // 條件沒變時 queryKey 不變，只靠 state 變化不會重取，所以明確 reload()。
            reload();
          }}
          onReset={() => {
            resetSearch();
          }}
        >
          <FormGrid uiId={`${pageId}-search-grid`} columns={2}>
            {/*
              FC-3：有契約欄位就畫契約欄位（整個主體區的超集）。
              legacySearchGroupsFor() 的退路分支（查詢欄位舊元件）已刪除——
              legacy-fallback-dead-path.test.ts 證明全站 156 頁沒有一頁需要它，
              那個舊元件從未被渲染過。
            */}
            {contractFields !== undefined ? (
              <LegacyContractFields
                pageId={pageId}
                fields={contractFields}
                values={draftFieldValues}
                onChange={(name, next) => {
                  setDraftFieldValues((current) => ({ ...current, [name]: next }));
                }}
                disabled={loading}
              />
            ) : null}
            {contractFields !== undefined ? null : (
              // R2（C2 findings F2）：這一頁有真正的查詢表單抽取資料時，關掉模板自帶的
              // 「關鍵字」／「狀態」兩個欄位——legacy 沒有這兩個欄位，畫出來只會被 diff
              // 演算法拿去跟 legacy 真正的欄位錯配（實測 legacy 的外鍵 select 被配到這裡的
              // 狀態下拉，1 筆「缺元素」變成 5 筆「屬性不同／文字不同」，見 report-R2.md）。
              // keyword／filterValue 這組 state 還留著：DataTableShell 的表內搜尋、
              // 匯出等仍會用到 `onKeywordChange={applyFilters}`，只是查詢區不重複畫欄位。
              <>
                <FormGridField uiId={`${pageId}-search-field-keyword`} label={t('search.keyword')}>
                  <TextInput
                    uiId={`${pageId}-search-input-keyword`}
                    value={draftKeyword}
                    onChange={setDraftKeyword}
                    disabled={loading}
                  />
                </FormGridField>
                {filterColumn === undefined ? null : (
                  <FormGridField
                    uiId={`${pageId}-search-field-${toUiKey(filterColumn.key)}`}
                    label={filterColumn.label}
                  >
                    <Select
                      uiId={`${pageId}-search-input-${toUiKey(filterColumn.key)}`}
                      value={draftFilterValue}
                      onChange={setDraftFilterValue}
                      disabled={loading}
                      // value="" 的提示項＝不過濾。原系統常見的「請選擇」空選項。
                      placeholder={t('unresolved.none')}
                      options={filterOptions.map((value) => ({ value, label: value }))}
                    />
                  </FormGridField>
                )}
              </>
            )}
          </FormGrid>
          {/* FC-5 R3（C1-c″）：查詢條件驗證訊息——原系統很多報表頁本來就會擋
              「沒選必要條件就送出」（例如「請選擇開始時間!」），不是這個頁型天生不能驗證，
              只是雛形之前沒做。 */}
          <ExtraValidationMessages uiId={`${pageId}-search`} messages={filterValidationMessages} />
        </SearchPanel>

        {error !== null ? (
          <TemplateError
            uiId={`${pageId}-error`}
            error={error}
            onRetry={reload}
            pageId={pageId}
            {...(messageKeyOverride === undefined ? {} : { messageKeyOverride })}
          />
        ) : (
          <DataTableShell
            uiId={`${pageId}-result`}
            page={page}
            pageSize={pageSize}
            total={total}
            keyword={keyword}
            loading={loading}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            onKeywordChange={(next) => {
              applyFilters(next, filterValue);
            }}
            // FC-5 R2（C1-c′）：這頁若有 legacy 的 empty 訊息就顯示它，否則退回 DataTableShell 預設。
            {...(legacyEmptyMessage === null ? {} : { emptyText: legacyEmptyMessage.text })}
            // D-1 第 2a 點：匯出端點與查詢端點不同，key 帶 `#export` 後綴。
            exportEffect={`list:${pageId}#export`}
            onExport={() => {
              void exportExcel();
            }}
          >
            <Table
              uiId={`${pageId}-result-table`}
              columns={tableColumns}
              rows={rows}
              rowKey={rowUiKey}
              // 空表文案由 DataTableShell 負責，避免同一畫面出現兩次。
              emptyText=""
            />
          </DataTableShell>
        )}
      </ContentPanel>
    </div>
  );
}
